import { useEffect, useRef, useState } from "react";
import type { CalendarEvent, EventsResponse } from "../../shared/types.js";
import { navigate } from "../router.js";
import { dayLabel, dayKey, relativeDayLabel, timeRange } from "../lib/eventTime.js";
import { BRANDING } from "../../shared/branding.js";
import { formatDescription } from "../lib/formatDescription.js";
import { EventCard } from "../components/EventCard.js";

function NextTrailDescription({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const clipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clipRef.current) return;
    setOverflowing(clipRef.current.scrollHeight > clipRef.current.clientHeight + 2);
  }, [html]);

  const showToggle = overflowing || expanded;

  return (
    <div className={`next-trail-description${expanded ? " is-expanded" : ""}`}>
      <div ref={clipRef} className="next-trail-description-clip">
        {formatDescription(html)}
      </div>
      {showToggle && !expanded && <div className="next-trail-description-fade" />}
      {showToggle && (
        <button
          type="button"
          className="next-trail-description-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function H5NextCard({ event, inPair }: { event: CalendarEvent; inPair?: boolean }) {
  return (
    <div className={`next-trail-card${inPair ? " next-trail-card--inpair" : ""}`}>
      <p className="next-trail-label">Next {BRANDING.appShortName} trail</p>
      <h2 className="next-trail-title">{event.title}</h2>
      <p className="next-trail-when">
        {relativeDayLabel(dayKey(event)) ?? dayLabel(dayKey(event))} · {timeRange(event)}
      </p>
      {event.location && (
        <p className="next-trail-location">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            📍 {event.location}
          </a>
        </p>
      )}
      {event.description && <NextTrailDescription html={event.description} />}
      <a
        className="btn btn-primary"
        href="/calendar"
        onClick={(e) => {
          e.preventDefault();
          navigate("/calendar");
        }}
      >
        See all events
      </a>
    </div>
  );
}

export function Home() {
  const [nextAny, setNextAny] = useState<CalendarEvent | null | undefined>(undefined);
  const [nextH5, setNextH5] = useState<CalendarEvent | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/events")
      .then((r) => r.json())
      .then((d: EventsResponse) => {
        if (cancelled) return;
        const primary = new Set(BRANDING.calendarPrimary.sourceIds);
        setNextAny(d.events[0] ?? null);
        setNextH5(d.events.find((e) => primary.has(e.sourceId)) ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setNextAny(null);
          setNextH5(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const nextIsH5 =
    nextAny != null && BRANDING.calendarPrimary.sourceIds.includes(nextAny.sourceId);

  return (
    <section className="page-wide">
      <div className="hero">
        <h1 className="hero-title">
          <span className="hero-title-primary">Honolulu Hawaii</span>
          <br />
          <span className="hero-title-secondary">Hash House Harriers</span>
        </h1>
        <p className="hero-subtitle">{BRANDING.heroDescription}</p>
      </div>

      {nextAny === undefined && (
        <div className="next-trail-card">
          <p className="muted">Loading next trail…</p>
        </div>
      )}

      {nextAny === null && (
        <div className="next-trail-card">
          <h2>Nothing on the calendar yet</h2>
          <p className="muted">Check the full calendar — schedules update often.</p>
          <a
            className="btn btn-primary"
            href="/calendar"
            onClick={(e) => {
              e.preventDefault();
              navigate("/calendar");
            }}
          >
            Open calendar
          </a>
        </div>
      )}

      {nextAny && nextIsH5 && <H5NextCard event={nextAny} />}

      {nextAny && !nextIsH5 && (
        <div className="next-trail-dual">
          <EventCard event={nextAny} />
          {nextH5 && <H5NextCard event={nextH5} inPair />}
        </div>
      )}

      <div className="home-blurb">
        <h2>What is hashing?</h2>
        <p>
          Hashing is a fine mixture of athleticism and sociability, debauchery, exploration, and adult humor. The Hares set the trail; the Pack chases.
          There's beer and fun at the end, sometimes in the middle too. We use only biodegradable markings and leave the trail cleaner than we found it.
        </p>
        <p>
          Just show up — but don't wear new shoes. Unless you want to know what happens if you do!
          Yes, yes, do wear new shoes, that's a great idea…
        </p>
        <p>
          The H5 is a <em>manuwahi</em> (free) hash, so BYO drinks and snacks for after the trail.
        </p>
      </div>

      <div className="home-outro">
        <p className="hero-tag">"A drinking club with a running problem."</p>
      </div>
    </section>
  );
}
